import { Facility } from '@reliance/types/models/Facility';
import { VisitationBooth } from '@reliance/types/models/VisitationBooth';
import React, { useCallback, useEffect, useState } from 'react';
import api from '../common/api';
import { IPaginatedResponse } from '../common/reliance-api-service';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import VisitationBoothList from '../visitation-booth/List';
import { useAlerts } from '../alert/AlertContext';

type FacilityVisitBoothsTabProps = {
  facility: Facility;
};

const FacilityVisitBoothsTab: React.FC<FacilityVisitBoothsTabProps> = ({ facility }) => {
  const [visitationBooths, setVisitationBooths] = useState<IPaginatedResponse<VisitationBooth> | null>(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editVisitationBooth, setEditVisitationBooth] = useState<VisitationBooth | null>(null);
  const [boothsLoading, setBoothsLoading] = useState(true);
  const alerts = useAlerts();

  const loadVisitationBooths = useCallback(() => {
    setBoothsLoading(true);
    return api
      .get<IPaginatedResponse<VisitationBooth>>('/visitation-booth', {
        params: { facilityId: facility.id, page: 1, perPage: 500 },
      })
      .then((res) => {
        setVisitationBooths(res.data);
        return res.data;
      })
      .catch(() => {
        alerts.error('An error occurred while loading visitation booths');
      })
      .finally(() => setBoothsLoading(false));
  }, [facility.id]);

  useNotifyTabLoading('visitation-booths', boothsLoading);

  useEffect(() => {
    void loadVisitationBooths();
  }, [loadVisitationBooths]);

  const saveVisitationBooth = (visitationBoothData: Record<string, unknown>) => {
    const payload: Record<string, unknown> = {
      ...visitationBoothData,
      facilityId: editVisitationBooth?.facility?.id ?? facility.id,
    };
    const id = payload.id as number | undefined;
    if (id) {
      return api.put(`/visitation-booth/${id}`, payload);
    }
    return api.post('/visitation-booth', payload);
  };

  return (
    <div className="jarviswidget">
      <div className="row">
        <div className="col-md-12 mb-3">
          <VisitationBoothList
            facility={facility}
            visitationBooths={visitationBooths}
            showAddEditModal={showAddEditModal}
            editVisitationBooth={editVisitationBooth}
            onAddClick={() => {
              setEditVisitationBooth(null);
              setShowAddEditModal(true);
            }}
            onEdit={(booth) => {
              setEditVisitationBooth(booth);
              setShowAddEditModal(true);
            }}
            onSave={(data) => {
              return saveVisitationBooth(data).then(() => {
                setEditVisitationBooth(null);
                setShowAddEditModal(false);
                void loadVisitationBooths();
              });
            }}
            onClose={() => {
              setEditVisitationBooth(null);
              setShowAddEditModal(false);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FacilityVisitBoothsTab;
